/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON texts of call errors (audit 6) and of the camera / microphone toast
 * (audit 9), from ion-spec design-tasks/call-errors.md, approved by the
 * owner (DECISIONS 66). All the wording is in this one file, so that a
 * change is one line here; upstream locales stay untouched.
 */

/** iON texts on the call error screen; false brings back upstream's. */
export const ION_CALL_ERROR_TEXTS: boolean = true;

export interface IonText {
  ru: string;
  en: string;
}

export const ACTION_TEXTS = {
  retry: { ru: "Повторить", en: "Try again" },
  reconnect: { ru: "Подключиться снова", en: "Reconnect" },
  rejoin: { ru: "Войти снова", en: "Rejoin" },
  report: { ru: "Сообщить о проблеме", en: "Report a problem" },
  reported: { ru: "Отправлено, спасибо", en: "Sent, thank you" },
  getIon: { ru: "Скачать iON", en: "Get iON" },
  copyLink: { ru: "Скопировать ссылку", en: "Copy link" },
  linkCopied: { ru: "Ссылка скопирована", en: "Link copied" },
  continueHere: { ru: "Продолжить здесь", en: "Continue here" },
  details: { ru: "Подробнее", en: "Details" },
  code: { ru: "Код", en: "Code" },
  reason: { ru: "Причина", en: "Reason" },
} satisfies Record<string, IonText>;

/** What the primary button of an error screen does. */
export type IonErrorAction = "retry" | "reconnect" | "rejoin" | "getIon";

export interface IonErrorTexts {
  title: IonText;
  description: IonText;
  action: IonErrorAction;
  /** Whether "Report a problem" is offered. */
  report: boolean;
}

const serviceUnavailable: IonErrorTexts = {
  title: {
    ru: "Звонки сейчас недоступны",
    en: "Calls are unavailable right now",
  },
  description: {
    ru: "Сервис звонков iON не отвечает. Попробуйте позвонить через несколько минут.",
    en: "The iON call service isn't responding. Try calling again in a few minutes.",
  },
  action: "retry",
  report: true,
};

const couldNotConnect: IonErrorTexts = {
  title: {
    ru: "Не удалось подключиться к звонку",
    en: "Couldn't connect to the call",
  },
  description: {
    ru: "Проверьте интернет и попробуйте ещё раз.",
    en: "Check your internet and try again.",
  },
  action: "retry",
  report: false,
};

export const CALL_ERROR_TEXTS = {
  serviceUnavailable,
  connectionLost: {
    title: { ru: "Связь прервалась", en: "Connection lost" },
    description: {
      ru: "Звонок отключился из-за плохой связи. Проверьте интернет и подключитесь снова.",
      en: "The call dropped because of a poor connection. Check your internet and reconnect.",
    },
    action: "reconnect",
    report: false,
  },
  membership: {
    title: { ru: "Звонок прервался", en: "The call was interrupted" },
    description: {
      ru: "Не удалось связаться с сервером iON. Проверьте интернет и войдите в звонок снова.",
      en: "We couldn't reach the iON server. Check your internet and rejoin the call.",
    },
    action: "rejoin",
    report: false,
  },
  startMedia: {
    ...couldNotConnect,
    description: {
      ru: "Проверьте интернет и попробуйте ещё раз. Если включён VPN — отключите его.",
      en: "Check your internet and try again. If you're using a VPN, turn it off.",
    },
  },
  capacity: {
    title: {
      ru: "Звонок сейчас недоступен",
      en: "The call is unavailable right now",
    },
    description: {
      ru: "Сервис звонков перегружен. Попробуйте войти через пару минут.",
      en: "The call service is busy. Try joining again in a couple of minutes.",
    },
    action: "retry",
    report: false,
  },
  roomCreation: {
    title: { ru: "Не удалось начать звонок", en: "Couldn't start the call" },
    description: {
      ru: "Сервис звонков не разрешил создать звонок. Попробуйте ещё раз, а если не получится — сообщите о проблеме.",
      en: "The call service didn't allow the call to start. Try again, and if it doesn't work, report the problem.",
    },
    action: "retry",
    report: true,
  },
  networkBlocked: {
    title: { ru: "Не удаётся соединиться", en: "Can't connect" },
    description: {
      ru: "Ваша сеть не пропускает звонок. Переключитесь на другой Wi-Fi или мобильный интернет и отключите VPN.",
      en: "Your network is blocking the call. Switch to another Wi-Fi or mobile data and turn off any VPN.",
    },
    action: "retry",
    report: false,
  },
  livekitConnection: couldNotConnect,
  openId: {
    title: { ru: "Не удалось войти в звонок", en: "Couldn't join the call" },
    description: {
      ru: "Сервер iON не подтвердил вход. Попробуйте ещё раз через минуту.",
      en: "The iON server didn't confirm your entry. Try again in a minute.",
    },
    action: "retry",
    report: true,
  },
  browser: {
    title: {
      ru: "Этот браузер не подходит",
      en: "This browser isn't supported",
    },
    description: {
      ru: "Браузер не поддерживает защищённые звонки. Откройте ссылку в свежем Chrome, Safari или Firefox — или в приложении iON.",
      en: "This browser doesn't support secure calls. Open the link in an up-to-date Chrome, Safari or Firefox, or in the iON app.",
    },
    action: "getIon",
    report: false,
  },
  unknown: {
    title: { ru: "Что-то пошло не так", en: "Something went wrong" },
    description: {
      ru: "Звонок прервался из-за ошибки. Попробуйте ещё раз. Если повторится — сообщите о проблеме, это поможет её исправить.",
      en: "The call stopped because of an error. Try again. If it happens again, report the problem so we can fix it.",
    },
    action: "retry",
    report: true,
  },
} satisfies Record<string, IonErrorTexts>;

export type IonCallErrorKind = keyof typeof CALL_ERROR_TEXTS;

/** The device whose start failed: "both" when camera and microphone did. */
export type IonMediaDevice = "camera" | "microphone" | "both";

/** Failures as livekit-client's MediaDeviceFailure names them. */
export type IonMediaFailure =
  | "PermissionDenied"
  | "NotFound"
  | "DeviceInUse"
  | "Other";

export const MEDIA_TOAST_TEXTS: Record<
  IonMediaFailure,
  Record<IonMediaDevice, IonText>
> = {
  PermissionDenied: {
    camera: { ru: "Нет доступа к камере", en: "No access to the camera" },
    microphone: {
      ru: "Нет доступа к микрофону",
      en: "No access to the microphone",
    },
    both: {
      ru: "Нет доступа к камере и микрофону",
      en: "No access to the camera and microphone",
    },
  },
  NotFound: {
    camera: { ru: "Камера не найдена", en: "Camera not found" },
    microphone: { ru: "Микрофон не найден", en: "Microphone not found" },
    both: {
      ru: "Камера и микрофон не найдены",
      en: "Camera and microphone not found",
    },
  },
  DeviceInUse: {
    camera: {
      ru: "Камера занята другим приложением",
      en: "Camera is used by another app",
    },
    microphone: {
      ru: "Микрофон занят другим приложением",
      en: "Microphone is used by another app",
    },
    both: {
      ru: "Камера и микрофон заняты другим приложением",
      en: "Camera and microphone are used by another app",
    },
  },
  Other: {
    camera: { ru: "Камера не включилась", en: "Couldn't turn on the camera" },
    microphone: {
      ru: "Микрофон не включился",
      en: "Couldn't turn on the microphone",
    },
    both: {
      ru: "Камера и микрофон не включились",
      en: "Couldn't turn on the camera and microphone",
    },
  },
};

/** Added to "no access": where to allow it (no Settings button yet). */
export const PERMISSION_HINTS = {
  browser: {
    ru: "Разрешите доступ в настройках браузера",
    en: "Allow access in your browser settings",
  },
  phone: {
    ru: "Разрешите доступ в настройках телефона",
    en: "Allow access in your phone settings",
  },
} satisfies Record<string, IonText>;

/** Screens and toasts outside the call error screen (design 14–17). */
export const OTHER_ERROR_TEXTS = {
  generic: {
    title: { ru: "Не удалось открыть звонок", en: "Couldn't open the call" },
    description: {
      ru: "Попробуйте ещё раз. Если повторится — сообщите о проблеме.",
      en: "Try again. If it happens again, report the problem.",
    },
  },
  callNotFound: {
    title: { ru: "Звонок не найден", en: "Call not found" },
    description: {
      ru: "Ссылка устарела или звонок уже закончился. Попросите прислать новую ссылку.",
      en: "The link has expired or the call has ended. Ask for a new link.",
    },
  },
  openElsewhere: {
    title: {
      ru: "Звонок открыт в другой вкладке",
      en: "The call is open in another tab",
    },
    description: {
      ru: "Вернитесь в ту вкладку или продолжите здесь: там звонок закроется.",
      en: "Go back to that tab or continue here — the call will close there.",
    },
  },
  screenShare: {
    title: {
      ru: "Не удалось показать экран",
      en: "Couldn't share your screen",
    },
    description: { ru: "", en: "" },
  },
} satisfies Record<string, { title: IonText; description: IonText }>;

/** Picks the language of a text: Russian for "ru…", English otherwise. */
export function pick(text: IonText, language: string | undefined): string {
  return language?.toLowerCase().startsWith("ru") ? text.ru : text.en;
}
